import React, { useState } from 'react';
import { trackEvent, EVENTS } from '../../lib/analyticsV2';

const CalculatorV2 = () => {
  const [propertyValue, setPropertyValue] = useState(500000);
  const [termMonths, setTermMonths] = useState(180); // 15 anos
  const [adminFeePct, setAdminFeePct] = useState(15); // 15% total do consorcio
  const [interestRateAnnum, setInterestRateAnnum] = useState(10.5); // 10.5% ao ano financiamento
  
  // Constantes de cálculo didático simples
  const calculateConsorcio = () => {
    const principal = propertyValue;
    const adminFee = principal * (adminFeePct / 100);
    const totalCost = principal + adminFee;
    const monthlyInstallment = totalCost / termMonths;
    return { totalCost, monthlyInstallment, adminFee };
  };

  const calculateFinanciamento = () => {
    // Cálculo simplificado PRICE para fins educativos
    const principal = propertyValue; // Assumindo financiamento 100% para poder comparar a mesma base (irreal na prática, mas educativo)
    const rateMonthly = (interestRateAnnum / 100) / 12;
    const pmt = principal * (rateMonthly * Math.pow(1 + rateMonthly, termMonths)) / (Math.pow(1 + rateMonthly, termMonths) - 1);
    const totalCost = pmt * termMonths;
    const totalInterest = totalCost - principal;
    return { totalCost, monthlyInstallment: pmt, totalInterest };
  };

  const consorcio = calculateConsorcio();
  const financiamento = calculateFinanciamento();

  const handleSimulate = () => {
    trackEvent('calculator_simulate', {
      property_value: propertyValue,
      term_months: termMonths
    });
  };

  return (
    <section className="py-16 md:py-24 bg-muted/20 border-t border-border">
      <div className="container px-4 md:px-6 mx-auto max-w-5xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl mb-4">
            Simulador Educativo de Custos
          </h2>
          <p className="text-muted-foreground max-w-3xl mx-auto">
            Compare a carga de juros de um financiamento tradicional com a taxa de administração de um consórcio.
          </p>
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400 rounded-lg text-sm text-left max-w-3xl mx-auto flex items-start gap-2">
            <span className="font-bold">Aviso Legal:</span>
            <span>
              Esta calculadora usa juros compostos fixos e não considera inflação (INCC/IPCA), valor da entrada, lances embutidos ou seguro. 
              <strong> Não há garantia de contemplação no consórcio.</strong> O objetivo é apenas educacional.
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-4 p-6 border rounded-xl bg-card shadow-sm space-y-6">
            <h3 className="font-bold text-lg border-b pb-2">Premissas</h3>
            
            <div>
              <label className="block text-sm font-medium mb-1">Valor do Bem (R$)</label>
              <input 
                type="number" 
                value={propertyValue}
                onChange={(e) => setPropertyValue(Number(e.target.value))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(propertyValue)}</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Prazo (Meses)</label>
              <input 
                type="number" 
                value={termMonths}
                onChange={(e) => setTermMonths(Number(e.target.value))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Taxa Adm Consórcio (%) Total</label>
              <input 
                type="number" 
                value={adminFeePct}
                onChange={(e) => setAdminFeePct(Number(e.target.value))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Juros Financiamento (% a.a.)</label>
              <input 
                type="number" 
                step="0.1"
                value={interestRateAnnum}
                onChange={(e) => setInterestRateAnnum(Number(e.target.value))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <button 
              onClick={handleSimulate}
              className="w-full inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90"
            >
              Recalcular
            </button>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            <div className="p-6 border rounded-xl bg-card shadow-sm border-primary/20">
              <div className="inline-block rounded-lg bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-4">
                Consórcio
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Parcela Média Estimada</p>
                  <p className="text-3xl font-bold text-foreground">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(consorcio.monthlyInstallment)}
                  </p>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground">Custo Total (Bem + Taxa)</p>
                  <p className="text-xl font-semibold">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(consorcio.totalCost)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor pago apenas em Taxas</p>
                  <p className="text-lg text-red-500 font-medium">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(consorcio.adminFee)}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border rounded-xl bg-muted/30 shadow-sm opacity-90">
              <div className="inline-block rounded-lg bg-muted-foreground/10 px-3 py-1 text-sm font-medium text-muted-foreground mb-4">
                Financiamento
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Parcela Fixa (Price)</p>
                  <p className="text-3xl font-bold text-foreground">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(financiamento.monthlyInstallment)}
                  </p>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground">Custo Total (Bem + Juros)</p>
                  <p className="text-xl font-semibold">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(financiamento.totalCost)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor pago apenas em Juros</p>
                  <p className="text-lg text-red-500 font-medium">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(financiamento.totalInterest)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorV2;
